import React, { FC, useState } from 'react'
import usePasswordToggle from '../../hooks/usePasswordToggle'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import * as yup from 'yup'
import iconEdit from '../../assets/icons/iconEdit.svg'
import { Formik, FormikValues } from 'formik'
import { createRoleForRegister, createRoleForTable } from '../../utils/createRoleForRegister'
import styles from './DataForLogin.module.css'
import CustomInput from '../UI/CustomInput/CustomInput'
import s from '../../styles/common/Form.module.css'

import dotIcon from '../../assets/icons/dot.svg'

import { changeCurrentStaffDetails, getCurrentStaff } from '../../redux/actions/staffAction'

import { getMe } from '../../redux/actions/meAction'
import { ItemInfo } from '../ItemInfo/ItemInfo'
import CustomButton from '../UI/CustomButton/CustomButton'
import { useToast } from '../../context/ToastContext'

export type DataForLoginPropsType = {
  isMe?: boolean
  userId: number
  role: string | undefined
  email: string
  password: string
}

const DataForLogin: FC<DataForLoginPropsType> = ({ isMe, userId, role, password, email }) => {
  const [editMode, setEditMode] = useState(false)
  const [PasswordInputType, ToggleIcon] = usePasswordToggle()

  const { token, userRole, user } = useAppSelector((state) => state.auth)

  const { showSuccess, showError } = useToast()

  const dispatch = useAppDispatch()

  const validationSchema = yup.object().shape({
    role: yup
      .string()
      .required('Поле является обязательным')
      .oneOf(
        userRole === 'directorPlace'
          ? ([
              'Директор-Зал',
              'Зал',
              'Зал',
              'директор-Зал',
              'Директор-Зал',
              'директор-Зал',
            ] as const)
          : ([
              'Директор-Агент',
              'Агент',
              'агент',
              'директор-агент',
              'директор-Агент',
              'Директор-агент',
            ] as const),
        userRole === 'directorPlace'
          ? 'Роль может быть только' + ' Директор-Зал или Зал'
          : 'Роль может быть только' + ' Директор-Агент или Агент',
      ),
    email: yup
      .string()
      .email('К сожалению, почта введена неверно, попробуйте еще раз')
      .required('Поле является обязательным'),
    password: yup
      .string()
      .required('Поле является обязательным')
      .min(8, 'Пароль должен' + ' содержать' + ' минимум 8' + ' символов'),
  })

  const handleSubmitHandler = async (values: FormikValues) => {
    const resultAction = await dispatch(
      changeCurrentStaffDetails({
        token,
        email: values.email,
        role: createRoleForRegister(values.role),
        staffId: String(userId),
        password: values.password,
      }),
    )

    if (changeCurrentStaffDetails.rejected.match(resultAction)) {
      showError('Произошла ошибка при изменении данных')
    } else {
      showSuccess('Данные успешно изменены')
      setEditMode(false)
      isMe
        ? await dispatch(getMe({ token }))
        : dispatch(getCurrentStaff({ staffId: String(userId), token }))
    }
  }

  return (
    <div className={styles.dataForLogin}>
      {editMode ? (
        <div className={styles.editMode}>
          <Formik
            initialValues={{
              role: role ? createRoleForTable(role) : '',
              email: email ? email : '',
              password: password ? password : '',
            }}
            validateOnBlur
            onSubmit={(values) => handleSubmitHandler(values)}
            validationSchema={validationSchema}
          >
            {(props) => {
              const { values, touched, errors, handleChange, handleBlur } = props

              return (
                <>
                  <div className={styles.header}>
                    <h2 className={styles.title}>Личные данные</h2>
                    <div className={styles.btnGroup}>
                      <CustomButton
                        className={styles.cancelBtn}
                        title={'Отменить'}
                        onClick={() => setEditMode(false)}
                      />
                      <CustomButton
                        className={styles.submitBtn}
                        title={'Сохранить'}
                        onClick={() => handleSubmitHandler(values)}
                      />
                    </div>
                  </div>
                  <div className={styles.container}>
                    {isMe ||
                    (userRole === 'directorPlace' && user.employee.id === userId) ||
                    (userRole === 'directorAgent' && user.employee.id === userId) ? (
                      <div className={styles.roleBlock}>
                        <div className={styles.leftBlock}>Роль</div>
                        <div className={styles.rightBlock}>
                          <div className={styles.companyBlock}>{values.role}</div>
                          {touched.role && errors.role && (
                            <p style={{ textAlign: 'start' }} className={s.errorValidation}>
                              {errors.role}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.roleBlock}>
                        <div className={styles.leftBlock}>Роль</div>
                        <div className={styles.rightBlock}>
                          <CustomInput
                            type='text'
                            placeholder=''
                            name='role'
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.role}
                            className={
                              errors.role && touched.role
                                ? `${styles.inputError} ${styles.input}`
                                : styles.input
                            }
                          />
                          {touched.role && errors.role && (
                            <p style={{ textAlign: 'start' }} className={styles.errorValidation}>
                              {errors.role}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    <div className={styles.loginBlock}>
                      <div className={styles.leftBlock}>Электронная почта/Логин</div>
                      <div className={styles.rightBlock}>
                        <CustomInput
                          type='text'
                          placeholder=''
                          name='email'
                          required
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.email}
                          className={
                            errors.email && touched.email
                              ? `${styles.inputError} ${styles.input}`
                              : styles.input
                          }
                        />
                      </div>
                    </div>
                    <div className={styles.passwordBlock}>
                      <div className={styles.leftBlock}>Пароль</div>
                      <div className={styles.rightBlock}>
                        <div className={styles.passInput}>
                          <CustomInput
                            type={PasswordInputType}
                            placeholder=''
                            name='password'
                            required
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            className={
                              errors.password && touched.password
                                ? `${styles.inputError} ${styles.input}`
                                : styles.input
                            }
                          />
                          <div className={styles.eyeIcon}>{ToggleIcon}</div>
                        </div>
                        {touched.password && errors.password && (
                          <p style={{ textAlign: 'start' }} className={s.errorValidation}>
                            {errors.password}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )
            }}
          </Formik>
        </div>
      ) : (
        <div className={styles.loginDetailsBlock}>
          <div className={styles.loginDetailsHeader}>
            <h2 className={styles.loginDetailsTitle}>Данные для входа</h2>
            {userRole !== 'employeeAgent' && userRole !== 'employeePlace' && (
              <div className={styles.editIconBlock}>
                <CustomButton
                  iconSrc={iconEdit}
                  className={styles.editBtn}
                  title={''}
                  onClick={() => setEditMode(true)}
                />
              </div>
            )}
          </div>
          <div className={styles.loginDetailsContainer}>
            <ItemInfo title='Роль' value={createRoleForTable(role) ?? ''} />
            <ItemInfo title='Электронная почта/Логин' value={email} />
            {isMe ? (
              <ItemInfo title='Пароль' value={password} />
            ) : (
              <ItemInfo title='Пароль' value={'*****'} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default DataForLogin
